<script lang="ts" setup>
import type { RouteRecordRaw } from "vue-router"
import { ElMessage } from "element-plus"
import path from "path-browserify"
import { computed, type PropType } from "vue"
import { useRouter } from "vue-router"
import { buildReservationPendingRoute } from "@/features/reservation-pending/realtime"
import { useReservationPendingStore } from "@/store/modules/reservationPending"
import { isExternal } from "@/utils/validate"
import SidebarExpandIcon from "./SidebarExpandIcon.vue"
import SidebarItemLink from "./SidebarItemLink.vue"

const router = useRouter()
const reservationPendingStore = useReservationPendingStore()

const props = defineProps({
  item: {
    type: Object as PropType<RouteRecordRaw>,
    required: true,
  },
  isCollapse: {
    type: Boolean,
    default: false,
  },
  isFirstLevel: {
    type: Boolean,
    default: true,
  },
  basePath: {
    type: String,
    default: "",
  },
})

const alwaysShowRootMenu = computed(() => {
  return props.item.meta && props.item.meta.alwaysShow
})

const showingChildNumber = computed(() => {
  if (props.item.children) {
    const showingChildren = props.item.children.filter((item) => {
      return !(item.meta && item.meta.hidden)
    })
    return showingChildren.length
  }
  return 0
})

const theOnlyOneChild = computed(() => {
  if (showingChildNumber.value > 1) {
    return null
  }
  if (props.item.children) {
    for (const child of props.item.children) {
      if (!child.meta || !child.meta.hidden) {
        return child
      }
    }
  }
  // If there is no children, return itself with path removed,
  // because this.basePath already contains item's path information
  return { ...props.item, path: "" }
})

/**
 * 没有hidden为false的子菜单
 */
const theOnlyOneChildHasNoShownChild = computed(() => {
  if (!theOnlyOneChild.value?.children || theOnlyOneChild.value?.children.length === 0) {
    return true
  } else {
    return theOnlyOneChild.value.children?.every((item) => {
      return item.meta && item.meta.hidden
    })
  }
})

const resolvePath = (routePath: string) => {
  if (isExternal(routePath)) {
    return routePath
  }
  if (isExternal(props.basePath)) {
    return props.basePath
  }
  return path.resolve(props.basePath, routePath)
}

const menuLink = computed(() => {
  if (theOnlyOneChild.value?.name === "ReservationWorkbench" && reservationPendingStore.pendingCount > 0) {
    return router.resolve(buildReservationPendingRoute(reservationPendingStore.earliestPendingDate)).fullPath
  }
  return resolvePath(theOnlyOneChild.value?.path ?? "")
})

const showLockedReason = (item: RouteRecordRaw) => {
  ElMessage.warning(String(item.meta?.lockedReason || "该功能尚未开通"))
}
</script>

<template>
  <div v-if="!props.item.meta?.hidden" :class="{ 'simple-mode': props.isCollapse, 'first-level': props.isFirstLevel }">
    <template v-if="!alwaysShowRootMenu && theOnlyOneChild && theOnlyOneChildHasNoShownChild">
      <template v-if="theOnlyOneChild.meta?.locked">
        <el-tooltip :content="String(theOnlyOneChild.meta.lockedReason || '该功能尚未开通')" placement="right">
          <el-menu-item
            class="menu-item menu-item--locked"
            :index="`locked:${resolvePath(theOnlyOneChild.path)}`"
            @click="showLockedReason(theOnlyOneChild)"
          >
            <svg-icon
              v-if="theOnlyOneChild.meta.svgIcon"
              size="20px"
              class="sidebar-menu-icon mr-8px"
              :name="theOnlyOneChild.meta.svgIcon"
            />
            <template v-if="theOnlyOneChild.meta.title" #title>
              <span>{{ theOnlyOneChild.meta.title }}</span>
              <span class="menu-item__locked-tag">未开通</span>
            </template>
          </el-menu-item>
        </el-tooltip>
      </template>
      <SidebarItemLink v-else-if="theOnlyOneChild.meta" :to="menuLink">
        <el-menu-item class="menu-item" :index="resolvePath(theOnlyOneChild.path)">
          <svg-icon
            v-if="theOnlyOneChild.meta.svgIcon"
            size="20px"
            class="sidebar-menu-icon mr-8px"
            :name="theOnlyOneChild.meta.svgIcon"
          />
          <component v-else-if="theOnlyOneChild.meta.elIcon" :is="theOnlyOneChild.meta.elIcon" class="sidebar-menu-icon mr-8px" />
          <template v-if="theOnlyOneChild.meta.title" #title>
            <el-badge
              v-if="theOnlyOneChild.name === 'ReservationWorkbench'"
              class="reservation-menu-badge"
              :value="reservationPendingStore.pendingCount"
              :max="99"
              :hidden="reservationPendingStore.pendingCount === 0"
            >{{ theOnlyOneChild.meta.title }}</el-badge>
            <template v-else>{{ theOnlyOneChild.meta.title }}</template>
          </template>
        </el-menu-item>
      </SidebarItemLink>
    </template>
    <el-sub-menu
      v-else
      :index="resolvePath(props.item.path)"
      :expand-close-icon="SidebarExpandIcon"
      :expand-open-icon="SidebarExpandIcon"
      teleported
    >
      <template #title>
        <svg-icon v-if="props.item.meta && props.item.meta.svgIcon" :name="props.item.meta.svgIcon" size="20px" class="sidebar-menu-icon mr-8px" />
        <component v-else-if="props.item.meta && props.item.meta.elIcon" :is="props.item.meta.elIcon" class="sidebar-menu-icon mr-8px" />
        <span v-if="props.item.meta && props.item.meta.title" class="sidebar-menu-title" :title="props.item.meta.title">{{ props.item.meta.title }}</span>
      </template>
      <template v-if="props.item.children">
        <sidebar-item
          v-for="child in props.item.children"
          :key="child.path"
          :item="child"
          :is-collapse="props.isCollapse"
          :is-first-level="false"
          :base-path="resolvePath(child.path)"
        />
      </template>
    </el-sub-menu>
  </div>
</template>

<style lang="scss" scoped>
.simple-mode {
  &.first-level {
    :deep(.el-sub-menu) {
      .el-sub-menu__icon-arrow {
        display: none;
      }

      span {
        visibility: hidden;
      }
    }
  }
}

:deep(.el-sub-menu__title) {
  // 箭头已参与 flex 排版，无需保留组件默认的绝对定位箭头占位。
  padding-right: var(--tea-spacing-sm) !important;

  .el-sub-menu__icon-arrow {
    position: static;
    flex: none;
    width: var(--tea-font-size-body-lg);
    height: var(--tea-font-size-body-lg);
    margin: 0 0 0 var(--tea-spacing-xs);
    color: currentcolor;
    opacity: 0.65;
  }

  &:hover .el-sub-menu__icon-arrow {
    opacity: 1;
  }
}

:deep(.el-sub-menu.is-opened > .el-sub-menu__title .sidebar-expand-chevron) {
  transform: rotate(90deg);
}

:deep(.el-sub-menu.is-active > .el-sub-menu__title .el-sub-menu__icon-arrow) {
  opacity: 1;
}

:deep(.el-sub-menu__title .sidebar-menu-title) {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.first-level {
  .menu-item {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    height: var(--v3-sidebar-menu-item-height);
    color: var(--bussiness-sidebar-text-color);
  }
}

.sidebar-menu-icon,
.sidebar-menu-title {
  color: currentcolor;
}

.reservation-menu-badge {
  display: inline-flex;
  gap: var(--tea-spacing-xs);
  align-items: center;

  :deep(.el-badge__content.is-fixed) {
    position: static;
    flex-shrink: 0;
    transform: none;
  }
}

.menu-item--locked {
  cursor: not-allowed;
  opacity: 0.72;
}

.menu-item__locked-tag {
  margin-left: auto;
  font-size: 11px;
  color: var(--el-color-warning);
}
</style>
